// NextContactDialog — quick-action «Назначить следующий контакт» из списка сделок (R21.3).
// Dialog создания события календаря, жёстко привязанного к сделке (dealId фиксирован).
// Поля: title (prefill «Контакт: {deal.title}»), startAt/endAt, location.
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { DateTimePickerField } from '@/components/forms';
import { calendarApi } from '@/api/calendar';
import type { Deal } from '@/api/deals';
import { useAuth } from '@/auth/AuthContext';

const nextContactSchema = z.object({
  title: z.string().min(1, 'Название обязательно').max(200),
  location: z.string().max(200).optional().or(z.literal('')),
  startAt: z.string().min(1, 'Дата начала обязательна'),
  endAt: z.string().min(1, 'Дата окончания обязательна'),
});

type NextContactFormValues = z.infer<typeof nextContactSchema>;

interface NextContactDialogProps {
  /** Сделка, к которой привязывается событие (dealId фиксирован). */
  deal: Deal;
  /** Флаг открытия dialog. */
  open: boolean;
  /** Колбэк закрытия (отмена или успешный submit). */
  onClose: () => void;
}

/** Dialog quick-action «Назначить следующий контакт» для сделки. */
export function NextContactDialog({ deal, open, onClose }: NextContactDialogProps): JSX.Element {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { control, handleSubmit } = useForm<NextContactFormValues>({
    resolver: zodResolver(nextContactSchema),
    defaultValues: {
      title: `Контакт: ${deal.title}`,
      location: '',
      startAt: '',
      endAt: '',
    },
  });

  // U-24: ref-флаг закрывает окно «двойной клик/Enter до ре-рендера isPending=true» —
  // useMutation отдаёт snapshot (useSyncExternalStore), в замыкании isPending не виден
  // до ререндера; кнопка disabled — вторая линия защиты.
  const submittingRef = useRef(false);
  const mutation = useMutation({
    mutationFn: (values: NextContactFormValues) =>
      calendarApi.create({
        title: values.title,
        location: values.location || null,
        startAt: values.startAt,
        endAt: values.endAt,
        allDay: false,
        // Backend CalendarCreateRequest требует type; quick-action — всегда встреча.
        type: 'MEETING',
        dealId: deal.id,
        ...(currentUser ? { organizerUserId: currentUser.id } : {}),
        departmentId: deal.departmentId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      // R35 / U-5: из карточки сделки — инвалидируем сам deal, чтобы подтянуть nextContactAt.
      void queryClient.invalidateQueries({ queryKey: ['deal', deal.id] });
      onClose();
    },
    // U-24: после завершения мутации ref сбрасывается — форма снова submit-доступна.
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  const onSubmit: SubmitHandler<NextContactFormValues> = (values) => {
    if (submittingRef.current || mutation.isPending) return;
    submittingRef.current = true;
    mutation.mutate(values);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Назначить следующий контакт</DialogTitle>
      <DialogContent>
        {mutation.isError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Не удалось создать событие. Проверьте, что дата начала раньше даты окончания.
          </Alert>
        ) : null}
        <Stack
          component="form"
          id="next-contact-form"
          spacing={2}
          sx={{ mt: 1 }}
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Название"
                required
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Название события' }}
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <DateTimePickerField
              name="startAt"
              control={control}
              label="Начало"
              required
              aria-label="Дата начала"
            />
            <DateTimePickerField
              name="endAt"
              control={control}
              label="Окончание"
              required
              aria-label="Дата окончания"
            />
          </Stack>
          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="Место проведения"
                fullWidth
                inputProps={{ 'aria-label': 'Место проведения' }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button type="submit" form="next-contact-form" variant="contained" disabled={mutation.isPending}>
          Назначить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
